import { motion } from "motion/react";
import { usePrefersReducedMotion } from "@dolfin/ui";
import { useLocation } from "react-router-dom";
import { Search, LogOut, Sun, Moon } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useTheme } from "@/components/ThemeProvider";
import { Button } from "@/components/ui/button";
import { DevModeBadge } from "@dolfin/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const titles: Record<string, string> = {
  "/": "Overview",
  "/organisations": "Organisations",
  "/companies": "Companies",
  "/users": "Members",
  "/webhooks": "Webhooks",
  "/api-keys": "API Keys",
  "/logs": "Logs",
  "/settings": "Settings",
};

function initials(name?: string) {
  if (!name) return "??";
  return name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export function TopBar({ onOpenCommandPalette }: { onOpenCommandPalette?: () => void }) {
  const reducedMotion = usePrefersReducedMotion();
  const { activeClient, user, logout, devPersona } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const pageTitle = titles[location.pathname] ?? "Portal";

  return (
    <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-md">
      {/* Breadcrumbs */}
      <div className="flex min-w-0 items-center gap-1.5 text-sm">
        <span className="max-w-28 truncate text-muted-foreground sm:max-w-48">{activeClient?.clientName ?? "-"}</span>
        <span className="text-muted-foreground/60">/</span>
        <span className="font-bold text-foreground">{pageTitle}</span>
      </div>

      <div className="flex-1" />

      {devPersona && <DevModeBadge label={devPersona.label} />}

      {/* Search / command palette trigger */}
      <motion.button
        type="button"
        aria-label="Search pages or switch client"
        whileTap={reducedMotion ? undefined : { scale: 0.98 }}
        onClick={onOpenCommandPalette}
        className="flex shrink-0 items-center gap-2 rounded-full border border-border px-2.5 py-2 text-sm text-muted-foreground transition hover:bg-accent hover:text-foreground"
      >
        <Search className="size-3.5" />
        <span className="hidden w-40 text-left md:inline">Search or jump to…</span>
        <kbd className="hidden rounded border border-border px-1 py-px font-mono text-[10px] md:inline">⌘K</kbd>
      </motion.button>

      <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Toggle theme">
        {theme === "dark" ? <Sun className="size-5" /> : <Moon className="size-5" />}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full">
            <div className="flex size-7 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-secondary-foreground">
              {initials(user?.name ?? user?.email)}
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="flex flex-col">
            <span className="text-sm font-semibold">{user?.name ?? "Portal user"}</span>
            <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-destructive">
            <LogOut className="size-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
